import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import Layout from '../components/Layout.jsx';
import { clientService, employeeService, projectService, taskService } from '../services/apiService.jsx';
import { useAuth } from '../context/AuthContext.jsx';
import { exportTasksToExcel, exportTasksToPDF } from '../utils/taskReportExport.js';
import './TaskManagement.css';

ChartJS.register(ArcElement, Tooltip, Legend);

const managerRoles = ['admin', 'super_admin', 'hod', 'team lead', 'tl', 'manager'];
// Compact, read-only-status assigned-tasks board — requested for tl/hod/admin
// specifically (super_admin is treated as admin-equivalent here).
const reviewRoles = ['admin', 'super_admin', 'hod', 'tl', 'team lead'];

// task_details_table.task_description is varchar(100); keep the UI within it.
const MAX_TASK_DESCRIPTION = 100;

const STATUS_OPTIONS = ['Pending', 'In Progress', 'Completed'];
const STATUS_COLORS = { Pending: '#f0a92b', 'In Progress': '#3d8bfd', Completed: '#31a24c' };

const getData = (response) => {
  const body = response?.data;
  return Array.isArray(body) ? body : Array.isArray(body?.data) ? body.data : [];
};

// clientService / projectService already unwrap to res.data, which is the
// { success, data: [...] } envelope - so read one level deeper than getData.
const getList = (payload) =>
  Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : [];

// A recurring task is a single DB row spanning its whole from/to window (no
// per-day rows are ever created for it), so it should only ever appear once
// in a task list - this guards the table against rendering it twice even if
// a future data source were to overlap.
const dedupeById = (list) => {
  const seen = new Set();
  return list.filter((task) => {
    const id = task.task_id || task.Task_id || task.id;
    if (id == null) return true;
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

const taskId = (task) => task.task_id || task.Task_id || task.id;
const taskDescription = (task) => task.desp || task.Task_Name || task.task_description || 'Untitled task';
const taskStatus = (task) => String(task.status || task.task_status || 'PENDING').toUpperCase();
const taskClient = (task) => task.customer_name || task.client_other || '—';
const taskProject = (task) => task.project_name || task.project_other || '—';
const taskType = (task) => {
  if (Number(task.is_recurring) === 1) return 'Recurring';
  return task.frequency || task.type || (Number(task.Type_of_task) === 0 ? 'Daily' : 'One Time');
};
const normalizedStatus = (task) => {
  const status = taskStatus(task).replace(/[_-]+/g, ' ').trim();
  if (['COMPLETED', 'COMPLETE', 'CLOSED'].includes(status)) return 'Completed';
  if (status === 'IN PROGRESS' || status === 'INPROGRESS') return 'In Progress';
  return 'Pending';
};
const displayDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('en-GB');
};
// Recurring tasks are bounded by a from/to window rather than a single
// deadline, so the whole timeline needs to reach the assignee, not just the end date.
const taskDeadlineDisplay = (task) => {
  if (Number(task.is_recurring) === 1 && task.recurring_from && task.recurring_to) {
    return `${displayDate(task.recurring_from)} → ${displayDate(task.recurring_to)}`;
  }
  return displayDate(task.end_date || task.deadline || task.till_date);
};
const isManagementEmployee = (employee) => String(employee.department_name || '').trim().toLowerCase() === 'management';
const employeeName = (employee) => `${employee.first_name || ''} ${employee.last_name || ''}`.trim();
const employeeKey = (employee) => employee.employee_id || employee.employee_code;

const TaskManagement = () => {
  const { user } = useAuth();
  const role = String(user?.role || '').toLowerCase();
  const canAssign = managerRoles.includes(role);
  // hod/tl sit in the middle of the chain (admin -> hod -> tl -> employee):
  // they assign work down AND receive it from above, so they get a toggle
  // between "tasks I assigned" and "tasks assigned to me".
  const canReceiveAssignments = ['hod', 'tl', 'team lead'].includes(role);
  // Admin sits above the whole chain: instead of only the tasks they
  // personally assigned, they get every task in the org - employees, TLs and
  // HODs - so they can see completion status across the board.
  const isOrgRole = ['admin', 'super_admin'].includes(role);
  const [viewMode, setViewMode] = useState('team');
  // Admin-only date-wise Daily Task Management Excel
  const [reportDate, setReportDate] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  });
  const [reportDownloading, setReportDownloading] = useState(false);
  const effectiveCanAssign = canAssign && !(canReceiveAssignments && viewMode === 'mine');
  const isReviewRole = effectiveCanAssign && reviewRoles.includes(role);
  const userId = user?.employee_id || user?.employee_code || localStorage.getItem('employee_code');

  const [employees, setEmployees] = useState([]);
  const [assignedTasks, setAssignedTasks] = useState([]);
  const [myTasks, setMyTasks] = useState([]);
  const [orgTasks, setOrgTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Assign form (managers only)
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [taskRows, setTaskRows] = useState([{ description: '' }]);
  const [defaultDeadline, setDefaultDeadline] = useState('');
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [formClient, setFormClient] = useState('');
  const [formClientOther, setFormClientOther] = useState('');
  const [formProject, setFormProject] = useState('');
  const [formProjectOther, setFormProjectOther] = useState('');
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringFrom, setRecurringFrom] = useState('');
  const [recurringTo, setRecurringTo] = useState('');

  // Table
  const [activeTab, setActiveTab] = useState('pending');
  const [currentPage, setCurrentPage] = useState(1);
  const [remarks, setRemarks] = useState({});
  const pageSize = 8;

  // Reassign modal (send a Completed task back to its assignee for rework)
  const [reassignTarget, setReassignTarget] = useState(null);
  const [reassignRemark, setReassignRemark] = useState('');
  const [reassignBusy, setReassignBusy] = useState(false);

  // Assign-to-self modal (all roles)
  const [selfAssignOpen, setSelfAssignOpen] = useState(false);
  const [selfTaskRows, setSelfTaskRows] = useState([{ description: '' }]);
  const [selfClient, setSelfClient] = useState('');
  const [selfClientOther, setSelfClientOther] = useState('');
  const [selfProject, setSelfProject] = useState('');
  const [selfProjectOther, setSelfProjectOther] = useState('');
  const [selfIsRecurring, setSelfIsRecurring] = useState(false);
  const [selfRecurringFrom, setSelfRecurringFrom] = useState('');
  const [selfRecurringTo, setSelfRecurringTo] = useState('');
  const [selfDeadline, setSelfDeadline] = useState('');
  const [selfSubmitting, setSelfSubmitting] = useState(false);

  const showNotice = (type, message) => {
    setNotice({ type, message });
    window.setTimeout(() => setNotice(null), 4500);
  };

  const selectableEmployees = useMemo(
    () => (role === 'admin' || role === 'super_admin'
      ? employees.filter((employee) => !isManagementEmployee(employee))
      : employees),
    [employees, role],
  );
  const projectsForClient = useMemo(() => {
    if (!formClient || formClient === 'others') return projects;
    return projects.filter((project) => String(project.customer_id) === String(formClient));
  }, [projects, formClient]);
  const projectsForSelfClient = useMemo(() => {
    if (!selfClient || selfClient === 'others') return projects;
    return projects.filter((project) => String(project.customer_id) === String(selfClient));
  }, [projects, selfClient]);

  const loadTasks = useCallback(async () => {
    if (!userId) return;
    try {
      // Always fetch "my tasks" too - hod/tl both assign work down and
      // receive it from above, so their own queue must be ready to toggle to.
      const [mine, assigned, org] = await Promise.all([
        taskService.getMyTasks(userId),
        canAssign ? taskService.getAssignedTasks(userId) : Promise.resolve({ data: [] }),
        isOrgRole ? taskService.getAllTasks() : Promise.resolve({ data: [] }),
      ]);
      setMyTasks(dedupeById(getData(mine)));
      setAssignedTasks(dedupeById(getData(assigned)));
      setOrgTasks(dedupeById(getData(org)));
    } catch (error) {
      console.error('Unable to load tasks', error);
      showNotice('danger', 'Tasks could not be loaded. Please try again.');
    }
  }, [canAssign, isOrgRole, userId]);

  const loadEmployees = useCallback(async () => {
    if (!canAssign) return;
    try {
      const response = role === 'admin' || role === 'super_admin'
        ? await employeeService.getAllEmployees()
        : role === 'hod'
          ? await employeeService.getHodReportingEmployees()
          : await employeeService.getReportingEmployees();
      setEmployees(getData(response));
    } catch (error) {
      console.error('Unable to load reporting employees', error);
      showNotice('danger', 'Employees could not be loaded. Please try again.');
    }
  }, [canAssign, role]);

  const loadRefData = useCallback(async () => {
    // Loaded for every role (not just managers) - the "Assign to Self" form
    // needs client/project options too.
    try {
      const [clientRes, projectRes] = await Promise.all([
        clientService.getClients().catch(() => []),
        projectService.getAllProjects().catch(() => []),
      ]);
      setClients(getList(clientRes));
      setProjects(getList(projectRes));
    } catch (error) {
      console.error('Unable to load client/project data', error);
    }
  }, []);

  useEffect(() => {
    const loadPage = async () => {
      setLoading(true);
      await Promise.all([loadEmployees(), loadTasks(), loadRefData()]);
      setLoading(false);
    };
    loadPage();
  }, [loadEmployees, loadTasks, loadRefData]);

  const tasks = effectiveCanAssign ? (isOrgRole ? orgTasks : assignedTasks) : myTasks;

  const grouped = useMemo(() => {
    const out = { Pending: [], 'In Progress': [], Completed: [] };
    tasks.forEach((task) => { out[normalizedStatus(task)].push(task); });
    return out;
  }, [tasks]);

  const stats = useMemo(() => {
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    let overdue = 0;
    tasks.forEach((task) => {
      if (normalizedStatus(task) === 'Completed') return;
      const deadline = task.end_date || task.deadline || task.till_date;
      if (!deadline) return;
      const date = new Date(deadline);
      if (!Number.isNaN(date.getTime()) && date < startOfToday) overdue += 1;
    });
    return {
      total: tasks.length,
      pending: grouped.Pending.length,
      inProgress: grouped['In Progress'].length,
      completed: grouped.Completed.length,
      overdue,
    };
  }, [tasks, grouped]);

  const breakdown = useMemo(() => {
    const out = {};
    STATUS_OPTIONS.forEach((status) => {
      const byPerson = new Map();
      grouped[status].forEach((task) => {
        const name = task.employee_name || task.assigned_to_name
          || (effectiveCanAssign ? (task.employee_id ? `Employee #${task.employee_id}` : 'Unassigned') : 'You');
        if (!byPerson.has(name)) byPerson.set(name, { items: [], role: task.employee_role });
        byPerson.get(name).items.push(taskDescription(task));
      });
      out[status] = [...byPerson.entries()]
        .map(([name, entry]) => ({ name, items: entry.items, role: entry.role }))
        .sort((a, b) => b.items.length - a.items.length);
    });
    return out;
  }, [grouped, effectiveCanAssign]);

  // Which status the breakdown panel is showing, and which employee row (if
  // any) is expanded within it. Clicking a pie slice jumps the panel to it.
  const [chartStatus, setChartStatus] = useState('Pending');
  const [expandedEmployee, setExpandedEmployee] = useState(null);
  useEffect(() => { setExpandedEmployee(null); }, [chartStatus]);

  const pieData = useMemo(() => ({
    labels: STATUS_OPTIONS,
    datasets: [{
      data: [stats.pending, stats.inProgress, stats.completed],
      backgroundColor: STATUS_OPTIONS.map((status) => STATUS_COLORS[status]),
      borderColor: '#ffffff',
      borderWidth: 2,
      hoverOffset: 6,
    }],
  }), [stats]);

  const pieOptions = useMemo(() => ({
    maintainAspectRatio: false,
    onClick: (_event, elements) => {
      if (elements?.length) setChartStatus(STATUS_OPTIONS[elements[0].index]);
    },
    onHover: (event, elements) => {
      if (event?.native?.target) event.native.target.style.cursor = elements?.length ? 'pointer' : 'default';
    },
    plugins: {
      legend: { position: 'bottom', labels: { boxWidth: 12, padding: 14, font: { size: 11 } } },
      tooltip: {
        padding: 10,
        titleFont: { size: 12 },
        bodyFont: { size: 11 },
        callbacks: {
          label: (ctx) => ` ${ctx.label}: ${ctx.parsed} task${ctx.parsed === 1 ? '' : 's'} — click for details`,
        },
      },
    },
  }), []);

  const pendingList = useMemo(
    () => [...grouped.Pending, ...grouped['In Progress']],
    [grouped],
  );
  const visibleTasks = activeTab === 'pending' ? pendingList : grouped.Completed;
  const totalPages = Math.max(1, Math.ceil(visibleTasks.length / pageSize));
  const paginatedTasks = visibleTasks.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  useEffect(() => { setCurrentPage(1); }, [activeTab]);
  useEffect(() => { setCurrentPage((page) => Math.min(page, totalPages)); }, [totalPages]);

  const patchTask = (id, patch) => {
    const apply = (list) => list.map((item) => (taskId(item) === id ? { ...item, ...patch } : item));
    setMyTasks(apply);
    setAssignedTasks(apply);
    setOrgTasks(apply);
  };

  const updateRow = (index, value) => {
    setTaskRows((rows) => rows.map((row, rowIndex) => (rowIndex === index
      ? { description: value.slice(0, MAX_TASK_DESCRIPTION) }
      : row)));
  };
  const removeRow = (index) => {
    setTaskRows((rows) => (rows.length === 1 ? rows : rows.filter((_, rowIndex) => rowIndex !== index)));
  };

  const resetForm = () => {
    setTaskRows([{ description: '' }]);
    setSelectedEmployee('');
    setFormClient('');
    setFormClientOther('');
    setFormProject('');
    setFormProjectOther('');
    setDefaultDeadline('');
    setIsRecurring(false);
    setRecurringFrom('');
    setRecurringTo('');
  };

  const submitTasks = async (event) => {
    event.preventDefault();
    const validRows = taskRows.filter((row) => row.description.trim());
    if (!selectedEmployee) {
      showNotice('warning', 'Choose an employee to assign to.');
      return;
    }
    if (!validRows.length) {
      showNotice('warning', 'Add at least one task.');
      return;
    }
    if (formClient === 'others' && !formClientOther.trim()) {
      showNotice('warning', 'Enter the client name.');
      return;
    }
    if (formProject === 'others' && !formProjectOther.trim()) {
      showNotice('warning', 'Enter the project name.');
      return;
    }
    if (isRecurring) {
      if (!recurringFrom || !recurringTo) {
        showNotice('warning', 'Choose both the recurring "from" and "to" dates.');
        return;
      }
      if (recurringFrom > recurringTo) {
        showNotice('warning', 'Recurring "from" date must be on or before the "to" date.');
        return;
      }
    } else if (!defaultDeadline) {
      showNotice('warning', 'Deadline is required.');
      return;
    }
    setSubmitting(true);
    try {
      const response = await taskService.createTask({
        s_id: userId,
        r_id: selectedEmployee,
        desp: validRows.map((row) => row.description.trim().slice(0, MAX_TASK_DESCRIPTION)),
        frequency: validRows.map(() => (isRecurring ? 'Daily' : 'One Time')),
        deadline: validRows.map(() => defaultDeadline || null),
        till_date: validRows.map(() => null),
        customer_id: formClient && formClient !== 'others' ? formClient : null,
        project_id: formProject && formProject !== 'others' ? formProject : null,
        client_other: formClient === 'others' ? formClientOther.trim() : null,
        project_other: formProject === 'others' ? formProjectOther.trim() : null,
        is_recurring: isRecurring ? 1 : 0,
        recurring_from: isRecurring ? recurringFrom : null,
        recurring_to: isRecurring ? recurringTo : null,
      });
      const emailSent = response?.data?.data?.emailSent ?? response?.data?.emailSent;
      resetForm();
      showNotice(
        'success',
        `${validRows.length} task${validRows.length > 1 ? 's' : ''} assigned${emailSent === false ? ' (email notification could not be sent)' : ''}.`,
      );
      loadTasks();
    } catch (error) {
      console.error('Unable to assign tasks', error);
      showNotice('danger', error.response?.data?.message || 'Tasks could not be assigned.');
    } finally {
      setSubmitting(false);
    }
  };

  const changeTaskStatus = async (task, status) => {
    const id = taskId(task);
    if (!id || !status) return;
    try {
      await taskService.updateTask(id, { status });
      patchTask(id, { status });
      showNotice('success', `Task marked as ${status}.`);
    } catch (error) {
      console.error('Unable to update task status', error);
      showNotice('danger', 'Task status could not be updated.');
    }
  };

  const saveRemark = async (task, value) => {
    const id = taskId(task);
    if (!id || value === (task.remarks ?? '')) return;
    try {
      await taskService.updateTask(id, { remarks: value });
      patchTask(id, { remarks: value });
    } catch (error) {
      console.error('Unable to save remark', error);
      showNotice('danger', 'Remark could not be saved.');
    }
  };

  const removeTask = async (task) => {
    const id = taskId(task);
    if (!id || !window.confirm('Delete this task? This cannot be undone.')) return;
    try {
      await taskService.deleteTask(id);
      const drop = (list) => list.filter((item) => taskId(item) !== id);
      setMyTasks(drop);
      setAssignedTasks(drop);
      setOrgTasks(drop);
      showNotice('success', 'Task deleted.');
    } catch (error) {
      console.error('Unable to delete task', error);
      showNotice('danger', 'Task could not be deleted.');
    }
  };

  const openReassign = (task) => {
    setReassignTarget(task);
    setReassignRemark('');
  };

  // "Reassign" on a Completed task is a send-back-for-rework: it goes back
  // to the same assignee (status resets to Pending) with a remark
  // explaining what needs fixing - there's no employee to pick.
  const submitReassign = async (event) => {
    event.preventDefault();
    if (!reassignTarget) return;
    setReassignBusy(true);
    try {
      const response = await taskService.reassignTask(taskId(reassignTarget), {
        employee_id: reassignTarget.employee_id,
        remarks: reassignRemark.trim(),
      });
      const emailSent = response?.data?.data?.emailSent ?? response?.data?.emailSent;
      setReassignTarget(null);
      showNotice(
        'success',
        `Task sent back${emailSent === false ? ' (email notification could not be sent)' : ''}.`,
      );
      loadTasks();
    } catch (error) {
      console.error('Unable to reassign task', error);
      showNotice('danger', error.response?.data?.message || 'Task could not be reassigned.');
    } finally {
      setReassignBusy(false);
    }
  };

  const openSelfAssign = () => {
    setSelfTaskRows([{ description: '' }]);
    setSelfClient('');
    setSelfClientOther('');
    setSelfProject('');
    setSelfProjectOther('');
    setSelfIsRecurring(false);
    setSelfRecurringFrom('');
    setSelfRecurringTo('');
    setSelfDeadline('');
    setSelfAssignOpen(true);
  };

  const updateSelfRow = (index, value) => {
    setSelfTaskRows((rows) => rows.map((row, rowIndex) => (rowIndex === index
      ? { description: value.slice(0, MAX_TASK_DESCRIPTION) }
      : row)));
  };
  const removeSelfRow = (index) => {
    setSelfTaskRows((rows) => (rows.length === 1 ? rows : rows.filter((_, rowIndex) => rowIndex !== index)));
  };

  const submitSelfTask = async (event) => {
    event.preventDefault();
    const validRows = selfTaskRows.filter((row) => row.description.trim());
    if (!validRows.length) {
      showNotice('warning', 'Add at least one task.');
      return;
    }
    if (selfClient === 'others' && !selfClientOther.trim()) {
      showNotice('warning', 'Enter the client name.');
      return;
    }
    if (selfProject === 'others' && !selfProjectOther.trim()) {
      showNotice('warning', 'Enter the project name.');
      return;
    }
    if (selfIsRecurring) {
      if (!selfRecurringFrom || !selfRecurringTo) {
        showNotice('warning', 'Choose both the recurring "from" and "to" dates.');
        return;
      }
      if (selfRecurringFrom > selfRecurringTo) {
        showNotice('warning', 'Recurring "from" date must be on or before the "to" date.');
        return;
      }
    } else if (!selfDeadline) {
      showNotice('warning', 'Deadline is required.');
      return;
    }
    setSelfSubmitting(true);
    try {
      await taskService.createTask({
        s_id: userId,
        r_id: userId,
        desp: validRows.map((row) => row.description.trim().slice(0, MAX_TASK_DESCRIPTION)),
        frequency: validRows.map(() => (selfIsRecurring ? 'Daily' : 'One Time')),
        deadline: validRows.map(() => selfDeadline || null),
        till_date: validRows.map(() => null),
        customer_id: selfClient && selfClient !== 'others' ? selfClient : null,
        project_id: selfProject && selfProject !== 'others' ? selfProject : null,
        client_other: selfClient === 'others' ? selfClientOther.trim() : null,
        project_other: selfProject === 'others' ? selfProjectOther.trim() : null,
        is_recurring: selfIsRecurring ? 1 : 0,
        recurring_from: selfIsRecurring ? selfRecurringFrom : null,
        recurring_to: selfIsRecurring ? selfRecurringTo : null,
      });
      setSelfAssignOpen(false);
      showNotice('success', `${validRows.length} task${validRows.length > 1 ? 's' : ''} added to your queue.`);
      loadTasks();
    } catch (error) {
      console.error('Unable to assign task to self', error);
      showNotice('danger', error.response?.data?.message || 'Task could not be created.');
    } finally {
      setSelfSubmitting(false);
    }
  };

  const handleDailyReport = async () => {
    if (!reportDate) return;
    setReportDownloading(true);
    try {
      const response = await taskService.downloadDailyReport(reportDate);
      const disposition = response.headers['content-disposition'] || '';
      const name = disposition.match(/filename="?([^";]+)"?/i)?.[1] || `Daily_Task_Report_${reportDate}.xlsx`;
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = name;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      // Errors arrive as a Blob because of responseType: 'blob'.
      let message = 'Daily report could not be downloaded.';
      try { message = JSON.parse(await error.response.data.text()).message || message; } catch (parseError) { /* keep default */ }
      showNotice(error.response?.status === 404 ? 'warning' : 'danger', message);
    } finally {
      setReportDownloading(false);
    }
  };

  const handleExport = (kind) => {
    const scope = effectiveCanAssign
      ? (isOrgRole ? 'Organization Tasks Report' : 'Assigned Tasks Report')
      : 'My Tasks Report';
    const summary = {
      Total: stats.total,
      Pending: stats.pending,
      'In Progress': stats.inProgress,
      Completed: stats.completed,
      Overdue: stats.overdue,
    };
    const result = kind === 'excel'
      ? exportTasksToExcel(tasks, { scope })
      : exportTasksToPDF(tasks, { scope, summary });
    if (result && result.ok === false) {
      showNotice('warning', result.message);
    }
  };

  return (
    <Layout>
      <section className={`task-management task-shell container-fluid ${effectiveCanAssign ? 'manager-task-view' : 'employee-task-view'}`}>
        {notice && <div className={`alert alert-${notice.type} task-alert`} role="alert">{notice.message}</div>}

        <header className="task-hero">
          <div>
            <span className="task-eyebrow">WORKSPACE</span>
            <h1>{effectiveCanAssign ? (isOrgRole ? 'Organization task control' : 'Team task control') : 'My task workspace'}</h1>
            <p>{effectiveCanAssign
              ? 'Assign work, keep ownership clear, and track delivery in one place.'
              : 'Stay on top of your work, update progress, and keep your manager informed.'}</p>
          </div>
          <div className="task-hero-actions">
            {canReceiveAssignments && (
              <div className="view-mode-toggle" role="tablist" aria-label="Task view">
                <button type="button" className={viewMode === 'team' ? 'active' : ''} onClick={() => setViewMode('team')}>
                  <i className="bi bi-people"></i> Team tasks
                </button>
                <button type="button" className={viewMode === 'mine' ? 'active' : ''} onClick={() => setViewMode('mine')}>
                  <i className="bi bi-person-check"></i> My tasks
                </button>
              </div>
            )}
            <button type="button" className="self-assign-btn" onClick={openSelfAssign}>
              <i className="bi bi-person-plus"></i> Assign to Self
            </button>
            {isOrgRole && (
              <div className="daily-report-picker" title="Download the Daily Task Management Excel for a date">
                <input
                  type="date"
                  value={reportDate}
                  onChange={(event) => setReportDate(event.target.value)}
                  aria-label="Daily report date"
                />
                <button
                  type="button"
                  className="export-icon-btn"
                  onClick={handleDailyReport}
                  disabled={!reportDate || reportDownloading}
                  title="Download daily report (Excel) for this date"
                  aria-label="Download daily report for selected date"
                >
                  <i className={reportDownloading ? 'bi bi-hourglass-split' : 'bi bi-calendar-check'}></i>
                </button>
              </div>
            )}
            <div className="export-menu">
              <button
                type="button"
                className="export-icon-btn"
                onClick={() => handleExport('excel')}
                disabled={!tasks.length}
                title="Download Excel Report"
                aria-label="Download Excel Report"
              >
                <i className="bi bi-file-earmark-spreadsheet"></i>
              </button>
              <button
                type="button"
                className="export-icon-btn"
                onClick={() => handleExport('pdf')}
                disabled={!tasks.length}
                title="Download PDF Report"
                aria-label="Download PDF Report"
              >
                <i className="bi bi-file-earmark-pdf"></i>
              </button>
            </div>
            <div className="task-role-chip">
              <i className="bi bi-person-check"></i>
              {effectiveCanAssign ? (isOrgRole ? 'Admin view · org-wide' : 'Manager view') : (canReceiveAssignments ? 'My tasks view' : 'Employee view')}
            </div>
          </div>
        </header>

        <div className="task-metrics" aria-label="Task summary">
          <SummaryCard icon="bi bi-list-task" tone="blue" value={stats.total} label={effectiveCanAssign ? (isOrgRole ? 'All tasks (org-wide)' : 'Assigned by me') : 'My tasks'} />
          <SummaryCard icon="bi bi-hourglass-split" tone="amber" value={stats.pending} label="Pending" />
          <SummaryCard icon="bi bi-arrow-repeat" tone="indigo" value={stats.inProgress} label="In progress" />
          <SummaryCard icon="bi bi-check2-circle" tone="green" value={stats.completed} label="Completed" />
          <SummaryCard icon="bi bi-exclamation-triangle" tone="red" value={stats.overdue} label="Overdue" />
        </div>

        <section className="task-chart-card" aria-labelledby="chart-heading">
          <div className="task-chart-head">
            <div>
              <span className="section-kicker">STATUS MIX</span>
              <h2 id="chart-heading">Task distribution</h2>
              <p>Click a slice, or a tab below, to see {effectiveCanAssign ? 'which employees own those tasks' : 'the tasks in that status'}.</p>
            </div>
          </div>
          {tasks.length ? (
            <div className="task-chart-body">
              <div className="task-chart-wrap"><Pie data={pieData} options={pieOptions} /></div>
              <div className="task-breakdown">
                <div className="breakdown-tabs" role="tablist">
                  {STATUS_OPTIONS.map((status) => (
                    <button
                      key={status}
                      type="button"
                      className={chartStatus === status ? 'active' : ''}
                      style={chartStatus === status ? { '--tab-color': STATUS_COLORS[status] } : undefined}
                      onClick={() => setChartStatus(status)}
                    >
                      <i className="bi bi-circle-fill" style={{ color: STATUS_COLORS[status] }}></i>
                      {status} <span>{grouped[status].length}</span>
                    </button>
                  ))}
                </div>
                <div className="breakdown-list">
                  {breakdown[chartStatus].length ? breakdown[chartStatus].map((group) => {
                    const open = expandedEmployee === group.name;
                    return (
                      <div className={`breakdown-group ${open ? 'is-open' : ''}`} key={group.name}>
                        <button type="button" className="breakdown-group-head" onClick={() => setExpandedEmployee(open ? null : group.name)}>
                          <span className="breakdown-name">
                            {effectiveCanAssign ? <i className="bi bi-person-circle"></i> : null} {group.name}
                            {group.role && <span className="breakdown-role">{group.role}</span>}
                          </span>
                          <span className="breakdown-right">
                            <span className="breakdown-count">{group.items.length}</span>
                            <i className={`bi ${open ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                          </span>
                        </button>
                        {open && (
                          <ul className="breakdown-items">
                            {group.items.map((item, index) => <li key={index}>{item}</li>)}
                          </ul>
                        )}
                      </div>
                    );
                  }) : <p className="empty-state">No {chartStatus.toLowerCase()} tasks.</p>}
                </div>
              </div>
            </div>
          ) : (
            <p className="empty-state">No tasks yet — the chart will populate once tasks exist.</p>
          )}
        </section>

        {effectiveCanAssign && (
          <section className="task-form-card" aria-labelledby="assign-heading">
            <div className="legacy-page-title">
              <div>
                <span className="section-kicker">ADD WORK</span>
                <h2 id="assign-heading">Assign a task</h2>
                <p>Log one or more tasks against a client and project, or set them to recur over a date range.</p>
              </div>
            </div>
            <form onSubmit={submitTasks}>
              <div className="task-form-grid">
                <label className="task-field">Select employee
                  <select className="form-select" value={selectedEmployee} onChange={(event) => setSelectedEmployee(event.target.value)}>
                    <option value="">Select employee</option>
                    {selectableEmployees.map((employee) => {
                      const key = employeeKey(employee);
                      return <option key={key} value={key}>{employeeName(employee) || key}{key ? ` (${key})` : ''}</option>;
                    })}
                  </select>
                </label>
                <label className="task-field">Select Client
                  <select className="form-select" value={formClient} onChange={(event) => { setFormClient(event.target.value); setFormProject(''); }}>
                    <option value="">Select client</option>
                    {clients.map((client) => (
                      <option key={client.customer_id} value={client.customer_id}>{client.customer_name}</option>
                    ))}
                    <option value="others">Others…</option>
                  </select>
                  {formClient === 'others' && (
                    <input className="form-control self-other-input" value={formClientOther} onChange={(event) => setFormClientOther(event.target.value)} placeholder="Enter client name" aria-label="Other client name" />
                  )}
                </label>
                <label className="task-field">Select Project
                  <select className="form-select" value={formProject} onChange={(event) => setFormProject(event.target.value)}>
                    <option value="">Select project</option>
                    {projectsForClient.map((project) => (
                      <option key={project.project_id} value={project.project_id}>{project.project_name}</option>
                    ))}
                    <option value="others">Others…</option>
                  </select>
                  {formProject === 'others' && (
                    <input className="form-control self-other-input" value={formProjectOther} onChange={(event) => setFormProjectOther(event.target.value)} placeholder="Enter project name" aria-label="Other project name" />
                  )}
                </label>
              </div>

              <h3 className="legacy-tasks-heading">Tasks <small>(max {MAX_TASK_DESCRIPTION} characters each)</small></h3>
              <div className="allocation-rows">
                {taskRows.map((row, index) => (
                  <div className="allocation-row" key={index}>
                    <input className="form-control" value={row.description} maxLength={MAX_TASK_DESCRIPTION} onChange={(event) => updateRow(index, event.target.value)} placeholder={`Task ${index + 1}`} aria-label={`Task ${index + 1}`} />
                    <button className="icon-button remove-button" type="button" onClick={() => removeRow(index)} disabled={taskRows.length === 1} aria-label="Remove task"><i className="bi bi-x-lg"></i></button>
                  </div>
                ))}
              </div>
              <div className="allocation-actions">
                <button className="add-task-button" type="button" onClick={() => setTaskRows((rows) => [...rows, { description: '' }])}><i className="bi bi-plus-lg"></i> Add Task</button>
              </div>

              <label className="recurring-toggle">
                <input
                  type="checkbox"
                  checked={isRecurring}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    setIsRecurring(checked);
                    if (checked) setDefaultDeadline('');
                  }}
                />
                Recurring task <small>(disables the deadline below — uses the date range instead)</small>
              </label>
              {isRecurring && (
                <div className="recurring-dates">
                  <label>From date<input className="form-control" type="date" required value={recurringFrom} onChange={(event) => setRecurringFrom(event.target.value)} /></label>
                  <label>To date<input className="form-control" type="date" required value={recurringTo} min={recurringFrom || undefined} onChange={(event) => setRecurringTo(event.target.value)} /></label>
                </div>
              )}

              <div className="legacy-form-group deadline-group">
                <label>Deadline
                  <input
                    className="form-control"
                    type="date"
                    required={!isRecurring}
                    disabled={isRecurring}
                    value={isRecurring ? '' : defaultDeadline}
                    onChange={(event) => setDefaultDeadline(event.target.value)}
                  />
                </label>
                {isRecurring && <small className="deadline-disabled-note">Deadline is disabled while "Recurring task" is on — the from/to dates above are used instead.</small>}
              </div>
              <button className="btn btn-primary assign-button" type="submit" disabled={submitting}>{submitting ? 'Assigning…' : 'Assign Task'}</button>
            </form>
          </section>
        )}

        <section className="task-table-card" aria-labelledby="tasks-heading">
          <div className="my-tasks-heading">
            <div>
              <span className="section-kicker">
                {effectiveCanAssign
                  ? (isOrgRole ? 'ALL TASKS · ORG-WIDE' : 'ASSIGNED TASKS')
                  : (canReceiveAssignments ? 'MY TASKS' : 'PERSONAL QUEUE')}
              </span>
              <h2 id="tasks-heading">Task management</h2>
            </div>
            <span className="task-count-label">
              {isReviewRole ? `${tasks.length} total` : `${visibleTasks.length} ${activeTab === 'pending' ? 'open' : 'completed'}`}
            </span>
          </div>

          {isReviewRole ? (
            <AssignedTasksBoard tasks={tasks} loading={loading} onDelete={removeTask} onReassign={openReassign} />
          ) : (
            <>
              <div className="task-tabs" role="tablist">
                <button className={activeTab === 'pending' ? 'active' : ''} type="button" onClick={() => setActiveTab('pending')}>Pending <span>{stats.pending + stats.inProgress}</span></button>
                <button className={activeTab === 'completed' ? 'active' : ''} type="button" onClick={() => setActiveTab('completed')}>Completed <span>{stats.completed}</span></button>
              </div>
              <TaskTable
                tasks={paginatedTasks}
                activeTab={activeTab}
                loading={loading}
                canManage={effectiveCanAssign}
                showAssignee={effectiveCanAssign}
                remarks={remarks}
                setRemarks={setRemarks}
                onStatusChange={changeTaskStatus}
                onRemarkSave={saveRemark}
                onDelete={removeTask}
                onReassign={openReassign}
              />
              <div className="task-pagination" aria-label="Task table pagination">
                <button type="button" onClick={() => setCurrentPage((page) => Math.max(1, page - 1))} disabled={currentPage === 1}>Prev</button>
                <span>Page {currentPage} of {totalPages}</span>
                <button type="button" onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))} disabled={currentPage === totalPages}>Next</button>
              </div>
            </>
          )}
        </section>
      </section>

      {canAssign && reassignTarget && (
        <div className="task-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="reassign-title" onMouseDown={(event) => { if (event.target === event.currentTarget) setReassignTarget(null); }}>
          <form className="task-modal" onSubmit={submitReassign}>
            <div className="task-modal-head">
              <h3 id="reassign-title">Reassign task</h3>
              <button type="button" className="task-modal-close" onClick={() => setReassignTarget(null)} aria-label="Close"><i className="bi bi-x-lg"></i></button>
            </div>
            <p className="task-modal-sub">
              <strong>#{taskId(reassignTarget)}</strong> — {taskDescription(reassignTarget)}<br />
              Sending this back to {reassignTarget.employee_name || 'the current assignee'} resets its status to Pending and emails them your remark.
            </p>
            <label className="task-field">Remark
              <textarea className="form-control" rows={3} value={reassignRemark} onChange={(event) => setReassignRemark(event.target.value)} placeholder="What needs to be fixed? (included in the email)" />
            </label>
            <div className="task-modal-actions">
              <button type="button" className="btn-ghost" onClick={() => setReassignTarget(null)} disabled={reassignBusy}>Cancel</button>
              <button type="submit" className="btn btn-primary assign-button" disabled={reassignBusy}>{reassignBusy ? 'Submitting…' : 'Submit'}</button>
            </div>
          </form>
        </div>
      )}

      {selfAssignOpen && (
        <div className="task-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="self-assign-title" onMouseDown={(event) => { if (event.target === event.currentTarget) setSelfAssignOpen(false); }}>
          <form className="task-modal task-modal-wide" onSubmit={submitSelfTask}>
            <div className="task-modal-head">
              <h3 id="self-assign-title">Assign to self</h3>
              <button type="button" className="task-modal-close" onClick={() => setSelfAssignOpen(false)} aria-label="Close"><i className="bi bi-x-lg"></i></button>
            </div>
            <p className="task-modal-sub">Log work you're picking up yourself. It lands straight in your own task queue.</p>

            <div className="task-form-grid">
              <label className="task-field">Select Client
                <select className="form-select" value={selfClient} onChange={(event) => { setSelfClient(event.target.value); setSelfProject(''); }}>
                  <option value="">Select client</option>
                  {clients.map((client) => (
                    <option key={client.customer_id} value={client.customer_id}>{client.customer_name}</option>
                  ))}
                  <option value="others">Others…</option>
                </select>
                {selfClient === 'others' && (
                  <input className="form-control self-other-input" value={selfClientOther} onChange={(event) => setSelfClientOther(event.target.value)} placeholder="Enter client name" aria-label="Other client name" />
                )}
              </label>
              <label className="task-field">Select Project
                <select className="form-select" value={selfProject} onChange={(event) => setSelfProject(event.target.value)}>
                  <option value="">Select project</option>
                  {projectsForSelfClient.map((project) => (
                    <option key={project.project_id} value={project.project_id}>{project.project_name}</option>
                  ))}
                  <option value="others">Others…</option>
                </select>
                {selfProject === 'others' && (
                  <input className="form-control self-other-input" value={selfProjectOther} onChange={(event) => setSelfProjectOther(event.target.value)} placeholder="Enter project name" aria-label="Other project name" />
                )}
              </label>
            </div>

            <h3 className="legacy-tasks-heading">Tasks <small>(max {MAX_TASK_DESCRIPTION} characters each)</small></h3>
            <div className="allocation-rows">
              {selfTaskRows.map((row, index) => (
                <div className="allocation-row" key={index}>
                  <input className="form-control" value={row.description} maxLength={MAX_TASK_DESCRIPTION} onChange={(event) => updateSelfRow(index, event.target.value)} placeholder={`Task ${index + 1}`} aria-label={`Task ${index + 1}`} />
                  <button className="icon-button remove-button" type="button" onClick={() => removeSelfRow(index)} disabled={selfTaskRows.length === 1} aria-label="Remove task"><i className="bi bi-x-lg"></i></button>
                </div>
              ))}
            </div>
            <div className="allocation-actions">
              <button className="add-task-button" type="button" onClick={() => setSelfTaskRows((rows) => [...rows, { description: '' }])}><i className="bi bi-plus-lg"></i> Add Task</button>
            </div>

            <label className="recurring-toggle">
              <input
                type="checkbox"
                checked={selfIsRecurring}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setSelfIsRecurring(checked);
                  if (checked) setSelfDeadline('');
                }}
              />
              Recurring task <small>(disables the deadline below — uses the date range instead)</small>
            </label>
            {selfIsRecurring && (
              <div className="recurring-dates">
                <label>From date<input className="form-control" type="date" required value={selfRecurringFrom} onChange={(event) => setSelfRecurringFrom(event.target.value)} /></label>
                <label>To date<input className="form-control" type="date" required value={selfRecurringTo} min={selfRecurringFrom || undefined} onChange={(event) => setSelfRecurringTo(event.target.value)} /></label>
              </div>
            )}

            <div className="legacy-form-group deadline-group">
              <label>Deadline
                <input
                  className="form-control"
                  type="date"
                  required={!selfIsRecurring}
                  disabled={selfIsRecurring}
                  value={selfIsRecurring ? '' : selfDeadline}
                  onChange={(event) => setSelfDeadline(event.target.value)}
                />
              </label>
              {selfIsRecurring && <small className="deadline-disabled-note">Deadline is disabled while "Recurring task" is on — the from/to dates above are used instead.</small>}
            </div>

            <div className="task-modal-actions">
              <button type="button" className="btn-ghost" onClick={() => setSelfAssignOpen(false)} disabled={selfSubmitting}>Cancel</button>
              <button type="submit" className="btn btn-primary assign-button" disabled={selfSubmitting}>{selfSubmitting ? 'Submitting…' : 'Submit'}</button>
            </div>
          </form>
        </div>
      )}
    </Layout>
  );
};

const SummaryCard = ({ icon, tone, value, label }) => (
  <div className="task-metric">
    <span className={`metric-icon metric-icon-${tone}`}><i className={icon}></i></span>
    <div><strong>{value}</strong><span>{label}</span></div>
  </div>
);

// tl / hod / admin board: every task in one scrollable list, status is
// read-only (set by the employee, not the reviewer), no remarks column, and
// each row is tinted by its status for quick scanning.
const STATUS_ROW_CLASS = {
  Completed: 'status-row-completed',
  'In Progress': 'status-row-progress',
  Pending: 'status-row-pending',
};
const STATUS_PILL_CLASS = {
  Completed: 'status-pill-completed',
  'In Progress': 'status-pill-progress',
  Pending: 'status-pill-pending',
};

const AssignedTasksBoard = ({ tasks, loading, onDelete, onReassign }) => (
  <div className="task-table-wrap task-table-scroll">
    <table className="table task-table">
      <thead>
        <tr>
          <th>Task ID</th>
          <th>Description</th>
          <th>Client</th>
          <th>Project</th>
          <th>Type</th>
          <th>Assignee</th>
          <th>Assigned By</th>
          <th>Assigned Date</th>
          <th>Deadline</th>
          <th>Status</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {loading ? (
          <tr><td colSpan={11} className="empty-state">Loading tasks…</td></tr>
        ) : tasks.length ? tasks.map((task) => {
          const id = taskId(task);
          const status = normalizedStatus(task);
          return (
            <tr key={id} className={STATUS_ROW_CLASS[status]}>
              <td>{id || '—'}</td>
              <td>{taskDescription(task)}</td>
              <td>{taskClient(task)}</td>
              <td>{taskProject(task)}</td>
              <td>{taskType(task)}</td>
              <td>
                {task.employee_name || task.assigned_to_name || task.employee_id || '—'}
                {task.employee_role && <span className="role-tag">{task.employee_role}</span>}
              </td>
              <td>{task.assigned_by_name || task.Assigned_by || task.assigned_by || '—'}</td>
              <td>{displayDate(task.create_date || task.assigned_date || task.timestamp)}</td>
              <td>{taskDeadlineDisplay(task)}</td>
              <td><span className={`status-pill ${STATUS_PILL_CLASS[status]}`}>{status}</span></td>
              <td>
                <div className="row-actions">
                  {status === 'Completed' && (
                    <button type="button" className="reassign-button" onClick={() => onReassign(task)}><i className="bi bi-arrow-left-right"></i> Reassign</button>
                  )}
                  <button type="button" className="delete-button" onClick={() => onDelete(task)}><i className="bi bi-trash"></i> Delete</button>
                </div>
              </td>
            </tr>
          );
        }) : (
          <tr><td colSpan={11} className="empty-state">No tasks found.</td></tr>
        )}
      </tbody>
    </table>
  </div>
);

const TaskTable = ({ tasks, activeTab, loading, canManage, showAssignee, remarks, setRemarks, onStatusChange, onRemarkSave, onDelete, onReassign }) => {
  const columnCount = 10 + (showAssignee ? 1 : 0) + (canManage ? 1 : 0);
  return (
    <div className="table-responsive task-table-wrap">
      <table className="table task-table">
        <thead>
          <tr>
            <th>Task ID</th>
            <th>Description</th>
            <th>Client</th>
            <th>Project</th>
            <th>Type</th>
            {showAssignee ? <th>Assignee</th> : null}
            <th>Assigned By</th>
            <th>Assigned Date</th>
            <th>Deadline</th>
            <th>Remarks</th>
            <th>Status</th>
            {canManage ? <th>Actions</th> : null}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={columnCount} className="empty-state">Loading tasks…</td></tr>
          ) : tasks.length ? tasks.map((task) => {
            const id = taskId(task);
            const status = normalizedStatus(task);
            return (
              <tr key={id}>
                <td>{id || '—'}</td>
                <td>{taskDescription(task)}</td>
                <td>{taskClient(task)}</td>
                <td>{taskProject(task)}</td>
                <td>{taskType(task)}</td>
                {showAssignee ? <td>{task.employee_name || task.assigned_to_name || task.employee_id || '—'}</td> : null}
                <td>{task.assigned_by_name || task.Assigned_by || task.assigned_by || '—'}</td>
                <td>{displayDate(task.create_date || task.assigned_date || task.timestamp)}</td>
                <td>{taskDeadlineDisplay(task)}</td>
                <td>
                  <input
                    className="form-control remarks-input"
                    value={remarks[id] ?? task.remarks ?? ''}
                    onChange={(event) => setRemarks((values) => ({ ...values, [id]: event.target.value }))}
                    onBlur={(event) => onRemarkSave(task, event.target.value)}
                    placeholder="Enter remark"
                  />
                </td>
                <td>
                  <select className="task-status-select" value={status} onChange={(event) => onStatusChange(task, event.target.value)}>
                    {STATUS_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
                  </select>
                </td>
                {canManage ? (
                  <td>
                    <div className="row-actions">
                      {status === 'Completed' && (
                        <button type="button" className="reassign-button" onClick={() => onReassign(task)}><i className="bi bi-arrow-left-right"></i> Reassign</button>
                      )}
                      <button type="button" className="delete-button" onClick={() => onDelete(task)}><i className="bi bi-trash"></i> Delete</button>
                    </div>
                  </td>
                ) : null}
              </tr>
            );
          }) : (
            <tr><td colSpan={columnCount} className="empty-state">No {activeTab} tasks found.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default TaskManagement;
